import React, { useState } from 'react';
import { Modal, Form, Select, Input, Alert, Space, Tag } from 'antd';
import { DealsApi } from 'api/api';
import { DealPaymentsDto, DealStage } from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import styled from 'styled-components';
import { getStageColor, getStageName } from 'pages/deals/constants';

const dealsApi = new DealsApi();

const StyledAlert = styled(Alert)`
  margin-bottom: 24px;
`;

interface Props {
  visible: boolean;
  dealId: number;
  currentStage: DealStage;
  dealPayments?: DealPaymentsDto | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const UpdateStageModal: React.FC<Props> = ({ visible, dealId, currentStage, dealPayments, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedStage, setSelectedStage] = useState<DealStage | null>(null);

  const stages = [
    { value: DealStage.InProgress, label: 'In Progress', icon: 'file-text' },
    { value: DealStage.ClosedWon, label: 'Closed (Won)', icon: 'check-circle' },
    { value: DealStage.ClosedLost, label: 'Closed (Lost)', icon: 'x-circle' },
  ].filter(stage => stage.value !== currentStage);

  const showNoPaymentsWarning =
    selectedStage === DealStage.ClosedWon && (!dealPayments?.schedule || dealPayments.schedule.length === 0);

  const showUnpaidPaymentsWarning =
    selectedStage === DealStage.ClosedWon &&
    (dealPayments?.schedule?.length ?? 0) > 0 &&
    dealPayments?.schedule?.some(payment => !payment.paidDate) &&
    (dealPayments?.totalAmount ?? 0) !== (dealPayments?.paidAmount ?? 0);

  const showUnpaidDealTotalWarning =
    selectedStage === DealStage.ClosedWon && (dealPayments?.totalAmount ?? 0) > (dealPayments?.paidAmount ?? 0);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await dealsApi.apiDealsIdStagePut({
        id: dealId,
        updateDealStageCommand: {
          id: dealId,
          stage: values.stage,
          notes: values.notes,
        },
      });

      onSuccess();
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('deals:update-stage', 'Update Deal Stage')}
      visible={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
    >
      <StyledAlert
        type="info"
        message={
          <Space>
            <span>{t('deals:current-stage', 'Current Stage')}:</span>
            <Tag color={getStageColor(currentStage)}>
              {t(getStageName(currentStage)?.key!, getStageName(currentStage)?.title!)}
            </Tag>
          </Space>
        }
        showIcon
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="stage"
          label={t('deals:new-stage', 'New Stage')}
          rules={[{ required: true, message: t('deals:stage-required', 'Please select a stage') }]}
        >
          <Select onChange={value => setSelectedStage(value)}>
            {stages.map(stage => (
              <Select.Option key={stage.value} value={stage.value}>
                <Space>
                  <FeatherIcon icon={stage.icon} size={16} />
                  {t(getStageName(stage.value)?.key!, getStageName(stage.value)?.title!)}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {showNoPaymentsWarning && (
          <StyledAlert
            type="warning"
            message={t(
              'deals:no-payments-warning',
              'You are about to mark this deal as won, but no payment schedule has been added.',
            )}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {showUnpaidDealTotalWarning && (
          <StyledAlert
            type="warning"
            message={t(
              'deals:unpaid-deal-total-warning',
              'You are about to mark this deal as won, but the deal total amount is not fully paid yet.',
            )}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {showUnpaidPaymentsWarning && !showUnpaidDealTotalWarning && (
          <StyledAlert
            type="warning"
            message={t(
              'deals:unpaid-payments-warning',
              'You are about to mark this deal as won, but not all scheduled payments have been marked as paid.',
            )}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="notes"
          label={t('deals:notes', 'Notes')}
          rules={[{ required: true, message: t('deals:notes-required', 'Please add notes about this change') }]}
        >
          <Input.TextArea
            rows={4}
            placeholder={t('deals:stage-notes-placeholder', 'Add notes about why you are changing the stage...')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateStageModal;
